import './GameControlButtons.styles.scss';

import { observer } from 'mobx-react';
import { InputNumber } from 'primereact/inputnumber';
import { Slider } from 'primereact/slider';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useStompClient } from 'react-stomp-hooks';

import { GameButton } from '../../model/GameButton';
import botManagementStore from '../../store/BotManagementStore';
import commonStore from '../../store/CommonStore';
import { formatNumber } from '../Game/table.render.utils';
import { GameControlButtonsProps } from './GameControlButtons.types';

const GameControlButtons: FC<GameControlButtonsProps> = ({
  tableId,
  room,
  tableBB,
  gameId,
  action,
  disabled,
  isVertical,
  onSuccessfulSendAction,
}) => {
  const { minBet, maxBet, betStep } = action;

  const [value, setValue] = useState(minBet);
  const [inputValue, setInputValue] = useState(minBet);

  const client = useStompClient();

  useEffect(() => {
    setValue(minBet);
    setInputValue(minBet);
  }, [action]);

  const buttons = useMemo(
    () =>
      action.buttons.map(button =>
        button.dynamicValue ? { ...button, amount: value } : { ...button },
      ),
    [action.buttons, value],
  );

  const sliderVisible = useMemo(
    () => maxBet > minBet && buttons.find(button => button.dynamicValue),
    [buttons, maxBet, minBet],
  );

  const isValueValid = (v: number): boolean => v <= maxBet && v >= minBet;

  const onSliderValueUpdate = (amount: number): void => {
    setValue(amount);
    setInputValue(amount);
  };

  const onInputValueUpdate = (amount: number): void => {
    setInputValue(amount);
    if (isValueValid(amount)) {
      setValue(amount);
    }
  };

  const onClick = (button: GameButton): void => {
    botManagementStore.manualAction(client, tableId, room, action.pid, {
      gameId,
      bet: button.amount,
      allIn: button.allIn,
      action: button.action,
    });
    onSuccessfulSendAction();
  };

  const renderBet = (amount: number): React.ReactNode => (
    <>
      {formatNumber(commonStore.convertMoney(amount, tableBB))}
      {commonStore.getIsChipsConvertedToBlinds() && (
        <span className="bb-text">{' BB'}</span>
      )}
    </>
  );

  const renderButton = (
    button: GameButton,
    dynamic = false,
  ): React.ReactNode => {
    const isValid = !dynamic || isValueValid(inputValue);
    return (
      <a
        key={`${button.amount}-${button.action}-${button.allIn}`}
        className={`action-button cursor-pointer ${
          !isValid ? ' action-not-valid' : ''
        }${button.aiSuggest && !disabled ? ' ai-suggest' : ''}${
          disabled ? ' disabled' : ''
        }`}
        onClick={() => isValid && !disabled && onClick(button)}
      >
        <div className="action-type">
          {button.allIn ? 'ALL IN' : button.action}
        </div>
        {button.amount > 0 && (
          <div className="action-amount">{renderBet(button.amount)}</div>
        )}
      </a>
    );
  };

  return (
    <div
      className={`GameControlButtons-Component${isVertical ? ' vertical' : ''}`}
      data-ignore-outside-click={true}
    >
      <div className="buttons-wrapper row p-2 gap-2">
        {buttons
          .filter(button => !button.dynamicValue)
          .map(button => renderButton(button))}
      </div>
      {sliderVisible && (
        <div className="slider-wrapper d-flex gap-4 mt-4 align-items-center">
          <InputNumber
            className="slider-input"
            value={inputValue}
            invalid={!isValueValid(inputValue)}
            min={0}
            max={maxBet}
            onValueChange={e => onInputValueUpdate(e.value)}
            onChange={e => onInputValueUpdate(e.value)}
            tooltip={
              !isValueValid(inputValue)
                ? `Choose value between ${minBet} and ${maxBet}`
                : undefined
            }
            tooltipOptions={{ position: 'bottom' }}
            disabled={disabled}
          />
          <span
            className={`slider-border${disabled ? ' disabled' : ''}`}
            onClick={() => !disabled && onSliderValueUpdate(minBet)}
          >
            {formatNumber(minBet)}
          </span>
          <Slider
            className="slider"
            orientation={isVertical ? 'vertical' : 'horizontal'}
            value={value}
            onChange={e => onSliderValueUpdate(e.value as number)}
            min={minBet}
            max={maxBet}
            step={betStep}
            disabled={disabled}
          />
          <span
            className={`slider-border${disabled ? ' disabled' : ''}`}
            onClick={() => !disabled && onSliderValueUpdate(maxBet)}
          >
            {formatNumber(maxBet)}
          </span>
          <div className="buttons-wrapper d-flex gap-2 p-2">
            {buttons
              .filter(button => button.dynamicValue)
              .map(button => renderButton(button, true))}
          </div>
        </div>
      )}
    </div>
  );
};

export default observer(GameControlButtons);
